class LilliputHeader extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.toggle = this.querySelector('[data-mobile-toggle]');
    this.mobileNav = this.querySelector('[data-mobile-nav]');
    this.nav = this.querySelector('[data-header-nav]');
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));
    this.openPanelId = null;
    this.closeTimer = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    if (this.toggle && this.mobileNav) {
      this.toggle.addEventListener('click', () => {
        this.setMobileOpen(!this.mobileNav.classList.contains('is-open'));
      });
    }

    this.triggers.forEach((trigger) => {
      const panelId = trigger.getAttribute('aria-controls');
      const item = trigger.closest('.ld-nav__item');

      if (item) {
        item.addEventListener('mouseenter', () => this.openMega(panelId));
        item.addEventListener('mouseleave', () => this.scheduleClose());
      }

      trigger.addEventListener('focus', () => this.openMega(panelId));
      trigger.addEventListener('click', (event) => {
        if (window.innerWidth < 990) return;
        if (this.openPanelId !== panelId) {
          event.preventDefault();
          this.openMega(panelId);
        }
      });
    });

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);

    this.syncStickyOffset = this.syncStickyOffset.bind(this);
    this.syncStickyOffset();
    this._resizeObserver = new ResizeObserver(this.syncStickyOffset);
    this._resizeObserver.observe(this);
    const headerSection = this.closest('.section-header');
    if (headerSection) this._resizeObserver.observe(headerSection);
    window.visualViewport?.addEventListener('resize', this.syncStickyOffset);
    window.addEventListener('resize', this.syncStickyOffset);
  }

  syncStickyOffset() {
    document.documentElement.style.setProperty('--ld-sticky-offset', '0px');
    const visualHeight = window.visualViewport?.height ?? window.innerHeight;
    document.documentElement.style.setProperty('--ld-visual-height', `${Math.round(visualHeight)}px`);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    window.visualViewport?.removeEventListener('resize', this.syncStickyOffset);
    window.removeEventListener('resize', this.syncStickyOffset);
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.closeMega();
      this.setMobileOpen(false);
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeMega();
    }
  }

  setMobileOpen(open) {
    if (!this.mobileNav || !this.toggle) return;
    this.mobileNav.classList.toggle('is-open', open);
    this.mobileNav.setAttribute('aria-hidden', open ? 'false' : 'true');
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  openMega(panelId) {
    this.clearCloseTimer();
    this.openPanelId = panelId;
    this.classList.add('is-mega-open');

    this.triggers.forEach((trigger) => {
      const active = trigger.getAttribute('aria-controls') === panelId;
      trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
      const item = trigger.closest('.ld-nav__item');
      if (item) item.classList.toggle('is-open', active);
    });

    this.panels.forEach((panel) => {
      const active = panel.id === panelId;
      panel.classList.toggle('is-open', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  closeMega() {
    this.clearCloseTimer();
    this.openPanelId = null;
    this.classList.remove('is-mega-open');

    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const item = trigger.closest('.ld-nav__item');
      if (item) item.classList.remove('is-open');
    });

    this.panels.forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('hidden', '');
    });
  }

  scheduleClose() {
    this.clearCloseTimer();
    this.closeTimer = window.setTimeout(() => this.closeMega(), 180);
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}

customElements.define('lilliput-header', LilliputHeader);
